import { useState } from "react";
import { Link } from "react-router-dom";
import { useContractors, type ContractorStatus } from "../api/contractors.js";

/**
 * Contractor list (ops O3). Defaults to the **review queue** — the
 * onboarding priority — and offers status tabs. Each row links to the
 * detail/approve screen.
 */
const TABS: { label: string; status?: string }[] = [
  { label: "Review queue", status: "PENDING_REVIEW,LEAD" },
  { label: "Approved", status: "APPROVED" },
  { label: "Suspended", status: "SUSPENDED" },
];

const statusStyles: Record<ContractorStatus, string> = {
  LEAD: "bg-field-gray text-charcoal",
  PENDING_REVIEW: "bg-amber-100 text-amber-800",
  APPROVED: "bg-emerald-100 text-emerald-800",
  SUSPENDED: "bg-rose-100 text-rose-700",
};

export function Contractors() {
  const [tab, setTab] = useState(0);
  const { data, isPending, isError, refetch } = useContractors(TABS[tab]!.status);
  const items = data?.items ?? [];

  return (
    <div>
      <h1 className="text-xl font-bold text-charcoal">Contractors</h1>

      <div className="mt-4 flex gap-1 border-b border-charcoal/15">
        {TABS.map((t, i) => (
          <button
            key={t.label}
            type="button"
            onClick={() => setTab(i)}
            className={[
              "px-4 py-2 text-sm font-medium",
              i === tab
                ? "border-b-2 border-brand-700 text-brand-800"
                : "text-charcoal hover:text-charcoal",
            ].join(" ")}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isPending ? (
        <p className="mt-6 text-sm text-charcoal">Loading…</p>
      ) : isError ? (
        <div className="mt-6">
          <p className="text-sm text-charcoal">Couldn&rsquo;t load contractors.</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-2 rounded-lg border border-charcoal/30 px-3 py-1.5 text-sm font-medium"
          >
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className="mt-6 text-sm text-charcoal">Nothing here.</p>
      ) : (
        <table className="mt-4 w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-charcoal/15 text-left text-xs uppercase tracking-wide text-charcoal">
              <th className="py-2 pr-4 font-medium">Business</th>
              <th className="py-2 pr-4 font-medium">Status</th>
              <th className="py-2 pr-4 font-medium">Services</th>
              <th className="py-2 pr-4 font-medium">Applied</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.contractorId} className="border-b border-charcoal/10 hover:bg-white">
                <td className="py-2 pr-4">
                  <Link
                    to={`/contractors/${c.contractorId}`}
                    className="font-medium text-brand-700 hover:underline"
                  >
                    {c.businessName}
                  </Link>
                  <div className="text-xs text-charcoal">{c.contactName}</div>
                </td>
                <td className="py-2 pr-4">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusStyles[c.status]}`}
                  >
                    {c.status.replace("_", " ")}
                  </span>
                </td>
                <td className="py-2 pr-4 text-charcoal">{c.serviceCategories.join(", ") || "—"}</td>
                <td className="py-2 pr-4 text-charcoal">
                  {new Date(c.createdAt).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
